import React, { useEffect, useState, useCallback, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { Bar } from 'react-chartjs-2';
import { useIntl, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import moment from 'moment';
import {Icon, Radio, Result, Row, Spin, Typography} from 'antd';
import { DASHBOARD_GRAPH_HOVER_BORDER_COLOUR, DASHBOARD_GRAPH_MAIN_COLOUR, DASHBOARD_GRAPH_MAIN_FADED_COLOUR } from '../dashboard-util';
import { setLoadingParameterValues } from '../../../../state/ui/inspections/inspectionListUiActions';
import routeLocations from '../../../../utility/route-locations';
import NoInspections from './NoInspections';
import {getDefaultLocale} from "../../../../configWeb";
import {
  addDaysToJSDate,
  addMonthsToJSDate,
  getDateComparisonNumber,
  getDateRangeForDashboardWidgetOption, isTimePeriodMovingUp
} from "../../bynx/packageUtil";

const { Text } = Typography;

/**
 * If there is no data we render this component instead.
 * We need to use some memoization to prevent the component re-rendering the graph constantly.
 */
function NoData() {
  const intl = useIntl();

  const dummyData = useMemo(() => ({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: intl.formatMessage({ id: 'widgetDataInspections' }),
        backgroundColor: DASHBOARD_GRAPH_MAIN_FADED_COLOUR,
        borderColor: DASHBOARD_GRAPH_MAIN_COLOUR,
        borderWidth: 2,
        hoverBackgroundColor: DASHBOARD_GRAPH_MAIN_COLOUR,
        hoverBorderColor: DASHBOARD_GRAPH_HOVER_BORDER_COLOUR,
        data: [65, 59, 80, 81, 56, 55, 40, 32, 78, 12, 18, 67],
      },
    ],
  }), [intl]);

  const renderDummyChart = useCallback(() => (
    <>
      <Bar data={dummyData} height={310} width={420} options={{ responsive: true, maintainAspectRatio: true }} />
      <Row type="flex" justify="center">
        <Radio.Group size="small">
          <Radio.Button value={7} checked><FormattedMessage id="widgetOptionsDays" /></Radio.Button>
          <Radio.Button value={2}><FormattedMessage id="widgetOptionsFortnight" /></Radio.Button>
          <Radio.Button value={4}><FormattedMessage id="widgetOptionsWeeks" /></Radio.Button>
          <Radio.Button value={12}><FormattedMessage id="widgetOptionsYear" /></Radio.Button>
        </Radio.Group>
      </Row>
    </>
  ), [dummyData]);

  return <NoInspections underlayComponent={renderDummyChart} />;
}


/**
 * A component for displaying the inspection rate.
 * @param {object} props The react props.
 */
function DashGraphInspectionRate({
  history, dashboardData, sessionUser, setLoadFilters, widgetSettings, updateWidgetNumbers, changeData, localLoading
}) {
  const intl = useIntl();

  // Real graph data
  const [labels, setLabels] = useState([]);
  const [series1, setSeries1] = useState([]);
  const [option, setOption] = useState(7);
  const [dateRanges, setDateRanges] = useState([]);
  const [noDataForOption, setNoDataForOption] = useState(false);

  const graph1Data = {
    labels,
    datasets: [
      {
        label: intl.formatMessage({ id: 'widgetDataInspections' }),
        backgroundColor: DASHBOARD_GRAPH_MAIN_FADED_COLOUR,
        borderColor: DASHBOARD_GRAPH_MAIN_COLOUR,
        borderWidth: 2,
        hoverBackgroundColor: DASHBOARD_GRAPH_MAIN_COLOUR,
        hoverBorderColor: DASHBOARD_GRAPH_HOVER_BORDER_COLOUR,
        data: series1,
      },
    ],
  };

  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  // Option load
  useEffect(() => {
    if (widgetSettings && widgetSettings.length > 0) {
      setOption(widgetSettings[2]);
    }
  }, [widgetSettings, setOption]);

  // Load data
  useEffect(() => {
    // Today's date
    const todayDate = new Date();
    // Option?
    if (option === 7) {
      // 7 Days
      const days = [];
      const inspections = [];
      const ranges = [];
      let optionInspectionCount = 0;
      setLabels([...Array(7)].map((v, index) => {
        const aDate = addDaysToJSDate(todayDate, -(6 - index));  //including today
        days.push(getDateComparisonNumber(aDate));
        inspections[index] = 0;
        ranges.push([moment(aDate).valueOf(), moment(aDate).valueOf()]);
        return aDate.toLocaleDateString([userLocale, getDefaultLocale().locale], { weekday: 'short' });
      }));
      if (dashboardData && dashboardData.inspectionsByDate && dashboardData.inspectionsByDate.length > 0) {
        dashboardData.inspectionsByDate.forEach(row => {
          let loopQueryDate = row.submittedQueryDate;
          const dtIndex = days.map(Number).indexOf(loopQueryDate);
          if (dtIndex > -1) {
            inspections[dtIndex] += row.inspectionCount; // can be more than one row per day (time)
            optionInspectionCount += row.inspectionCount;
          }
        });
      }
      setSeries1(inspections);
      setDateRanges(ranges);
      setNoDataForOption(optionInspectionCount === 0);
    } else if (option === 2 || option === 4) {
      // 2 or 4 weeks
      const startDates = [];
      const endDates = [];
      const inspections = [];
      const ranges = [];
      let optionInspectionCount = 0;
      setLabels([...Array(option)].map((v, index) => {
        let aDate = addDaysToJSDate(todayDate, (1 - ((option - index) * 7))); // last week including today etc
        startDates.push(new Date(aDate));
        aDate = addDaysToJSDate(aDate, 6);
        endDates.push(aDate);
        inspections[index] = 0;
        ranges.push([moment(startDates[index]).valueOf(), moment(endDates[index])]);
        return startDates[index].toLocaleDateString([userLocale, getDefaultLocale().locale], { datestyle: 'short' });
      }));
      if (dashboardData && dashboardData.inspectionsByDate && dashboardData.inspectionsByDate.length > 0) {
        dashboardData.inspectionsByDate.forEach(row => {
          let loopQueryDate = row.submittedQueryDate;
          [...Array(option)].forEach((v, index) => {
            if (loopQueryDate >= getDateComparisonNumber(startDates[index]) && loopQueryDate <= getDateComparisonNumber(endDates[index])) {
              inspections[index] += row.inspectionCount;
              optionInspectionCount += row.inspectionCount;
            }
          });
        });
      }
      setSeries1(inspections);
      setDateRanges(ranges);
      setNoDataForOption(optionInspectionCount === 0);
    } else if (option === 12) {
      // 12 months
      const startDates = [];
      const endDates = [];
      const inspections = [];
      const ranges = [];
      let optionInspectionCount = 0;
      setLabels([...Array(12)].map((v, index) => {
        const startOfMonth = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
        let aDate = addMonthsToJSDate(startOfMonth, -(11 - index));
        startDates.push(new Date(aDate));
        aDate = addMonthsToJSDate(aDate, 1);
        aDate = addDaysToJSDate(aDate, -1);
        endDates.push(aDate);
        inspections[index] = 0;
        ranges.push([moment(startDates[index]).valueOf(), moment(endDates[index]).valueOf()]);
        return aDate.toLocaleDateString([userLocale, getDefaultLocale().locale], { month: 'short' });
      }));
      if (dashboardData && dashboardData.inspectionsByDate && dashboardData.inspectionsByDate.length > 0) {
        dashboardData.inspectionsByDate.forEach(row => {
          let loopQueryDate = row.submittedQueryDate;
          [...Array(12)].forEach((v, index) => {
            if (loopQueryDate >= getDateComparisonNumber(startDates[index]) && loopQueryDate <= getDateComparisonNumber(endDates[index])) {
              inspections[index] += row.inspectionCount;
              optionInspectionCount += row.inspectionCount;
            }
          });
        });
      }
      setSeries1(inspections);
      setDateRanges(ranges);
      setNoDataForOption(optionInspectionCount === 0);
    }
  }, [dashboardData, userLocale, option]);

  // Click through
  const clickOnBar = (element) => {
    // Filters
    const filterObj = {
      my: false,
      compliance: false,
      other: false,
      defects: false,
      noDefects: false,
      faults: false,
      damages: false,
      costs: false,
    };

    // Go to list
    if (dateRanges && dateRanges.length > 0 && element && element.length > 0) {
      // eslint-disable-next-line no-underscore-dangle
      const passDates = dateRanges[element[0]._index];
      setLoadFilters({ ...filterObj, dates: passDates });
      history.push(routeLocations.inspectionList);
    }
  };

  // Change option and data
  const changeToNewOption = (newOption) => {
    // Must get data if moving up (already have if moving down)
    if (isTimePeriodMovingUp(option, newOption)) {
      const dateRange = getDateRangeForDashboardWidgetOption(newOption);
      changeData(dateRange[0], dateRange[1]);  //localLoading will be true during this
    }
    // Set new option
    setOption(newOption);
  };

  // No inspections (show dummy graph plus message)
  const hasInspections = dashboardData && dashboardData.inspectionStatistics && dashboardData.inspectionStatistics.totalInspections > 0;
  if (!hasInspections) return <NoData />;

  // GRAPH and switchers
  return (
    <div style={{ position: 'relative' }}>
      <Spin spinning={localLoading}>
        <Bar
          data={graph1Data}
          height={310}
          width={350}
          onElementsClick={clickOnBar}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            hover: {
              onHover(e) {
                // eslint-disable-next-line react/no-this-in-sfc
                const point = this.getElementAtEvent(e);
                if (point.length) e.target.style.cursor = 'pointer';
                else e.target.style.cursor = 'default';
              },
            },
            legend: {
              onClick(e) {
                e.stopPropagation();
              },
            },
            scales: {
              yAxes: [{
                ticks: {
                  beginAtZero: true,
                  callback(value) {
                    if (value % 1 === 0) { return value; }
                    return null;
                  },
                },
              }],
            },
          }}
        />
        { noDataForOption
            ? (
                <div style={{
                  position: 'absolute',
                  top: '59px',
                  width: '100%',
                  height: 300,
                }}
                >
                  <Result
                      icon={<Icon type="carry-out" theme="twoTone" />}
                      title={<FormattedMessage id="widgetMessageTitle" />}
                      style={{ marginTop: '-2.5em' }}
                      extra={(
                          <div style={{ fontWeight: 500 }}>
                            <Row><Text><FormattedMessage id="widgetMessage4" /></Text></Row>
                          </div>
                      )}
                  />
                </div>
            )
            : <></>
        }
        <Row type="flex" justify="center" align="middle" style={{ marginTop: '0.25em' }}>
          <Radio.Group
            size="small"
            value={option}
            onChange={e => {
              changeToNewOption(e.target.value);
              updateWidgetNumbers(widgetSettings[0], widgetSettings[1], e.target.value, widgetSettings[3], widgetSettings[4]);
            }}
          >
            <Radio.Button value={7}><FormattedMessage id="widgetOptionsDays" /></Radio.Button>
            <Radio.Button value={2}><FormattedMessage id="widgetOptionsFortnight" /></Radio.Button>
            <Radio.Button value={4}><FormattedMessage id="widgetOptionsWeeks" /></Radio.Button>
            <Radio.Button value={12}><FormattedMessage id="widgetOptionsYear" /></Radio.Button>
          </Radio.Group>
        </Row>
      </Spin>
    </div>
  );
}

DashGraphInspectionRate.propTypes = {
  dashboardData: PropTypes.oneOfType([PropTypes.object]),
  history: PropTypes.oneOfType([PropTypes.object]),
  sessionUser: PropTypes.shape({
    locale: PropTypes.string.isRequired,
  }).isRequired,
  setLoadFilters: PropTypes.func.isRequired,
  changeData: PropTypes.func.isRequired,
  localLoading: PropTypes.bool.isRequired,
  widgetSettings: PropTypes.arrayOf(PropTypes.number),
  updateWidgetNumbers: PropTypes.func,
};

DashGraphInspectionRate.defaultProps = {
  dashboardData: null,
  history: null,
  widgetSettings: [1, 1],
  updateWidgetNumbers: () => {},
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = () => ({});

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  setLoadFilters: paramObj => dispatch(setLoadingParameterValues(paramObj)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(DashGraphInspectionRate);
